import { type ReactNode } from "react";
import { Button, colors, figure } from "@cliqsuite/ui";
import { Link, useRouterState } from "@tanstack/react-router";
import { Modal } from "./Modal";
import { useIsDesktop, useIsWide } from "../../hooks/useIsDesktop";
import { useInstalledModules } from "../../lib/useInstalledModules";

/** The canvas KPI tile. `dark` is the lead tile of a row (ink background). */
export function StatCard({
  label,
  value,
  icon,
  note,
  noteTone,
  dark = false,
}: {
  label: string;
  value: string;
  icon: string;
  fg?: string;
  bg?: string;
  note?: string;
  noteTone?: "up" | "down";
  dark?: boolean;
}) {
  return (
    <div className={`o-stat${dark ? " is-dark" : ""}`}>
      <div className="o-stat__label">
        <span className="ms" aria-hidden="true">{icon}</span>
        {label}
      </div>
      <div className="o-stat__value num">{figure(value)}</div>
      {note && <div className={`o-stat__note num${noteTone ? ` is-${noteTone}` : ""}`}>{note}</div>}
    </div>
  );
}

/** Wraps a row of StatCards (or similar): a CSS grid that reflows above the tablet breakpoint, two across below it. */
export function StatGrid({
  children,
  minColWidth = 170,
  marginBottom = 0,
}: {
  children: ReactNode;
  minColWidth?: number;
  cardWidth?: number;
  marginBottom?: number;
}) {
  const isWide = useIsWide();
  if (isWide) {
    return <div style={{ display: "grid", gridTemplateColumns: `repeat(auto-fit, minmax(${minColWidth}px, 1fr))`, gap: 12, marginBottom: marginBottom || 18 }}>{children}</div>;
  }
  // Phone (canvas v2-om-*): two across, never a strip that runs off the screen.
  return (
    <div className="o-kpis" style={{ marginBottom }}>
      {children}
    </div>
  );
}

export function ConfirmDialog({
  title,
  body,
  confirmLabel = "Delete",
  onConfirm,
  onCancel,
}: {
  title: string;
  body: ReactNode;
  confirmLabel?: string;
  onConfirm: () => void;
  onCancel: () => void;
}) {
  return (
    <Modal
      title={title}
      onClose={onCancel}
      width={380}
      footer={
        <>
          <Button variant="secondary" onClick={onCancel}>
            Cancel
          </Button>
          <Button onClick={onConfirm} style={{ background: "#DC2D49" }}>
            {confirmLabel}
          </Button>
        </>
      }
    >
      <div style={{ fontSize: 13.5, color: colors.textSecondary, lineHeight: 1.6 }}>{body}</div>
    </Modal>
  );
}

/** The Delete button at the left of a sheet's footer. */
export function SheetDelete({ onClick }: { onClick: () => void }) {
  return (
    <Button variant="secondary" onClick={onClick} style={{ background: "#FFF1F4", color: "#DC2D49", marginRight: "auto" }}>
      <span className="ms" aria-hidden="true">delete</span> Delete
    </Button>
  );
}

export function Toast({ message }: { message: string | null }) {
  if (!message) return null;
  return (
    <div className="o-toast" role="status" aria-live="polite">
      {message}
    </div>
  );
}

export function useToast() {
  // Simple hook-free timer-based toast is easiest inline in each page; this helper
  // just standardizes the visual. Pages own the `toast` state + setTimeout to clear it.
  return { Toast };
}

/** Underline tabs under the page header, as on the canvas. */
export function Tabs({
  options,
  value,
  onChange,
}: {
  options: Array<{ key: string; label: string }>;
  value: string;
  onChange: (key: string) => void;
}) {
  return (
    <div className="o-tabs" role="tablist">
      {options.map((o) => (
        <button type="button" role="tab" aria-selected={o.key === value} key={o.key} onClick={() => onChange(o.key)}>
          {o.label}
        </button>
      ))}
    </div>
  );
}

/** Rounded filter chips (Bills' All/Open/Paid…). */
export function Chips({
  options,
  value,
  onChange,
}: {
  options: Array<{ key: string; label: string }>;
  value: string;
  onChange: (key: string) => void;
}) {
  return (
    <div className="o-chips" role="radiogroup">
      {options.map((o) => (
        <button type="button" role="radio" aria-checked={o.key === value} key={o.key} onClick={() => onChange(o.key)}>
          {o.label}
        </button>
      ))}
    </div>
  );
}

/** Tabs that are separate routes (Reports: Sales / Deep dive). */
export function RouteTabs({ options }: { options: Array<{ to: string; label: string }> }) {
  const pathname = useRouterState({ select: (s) => s.location.pathname });
  return (
    <div className="o-tabs" role="tablist">
      {options.map((o) => (
        <Link key={o.to} to={o.to} role="tab" aria-selected={pathname === o.to}>
          {o.label}
        </Link>
      ))}
    </div>
  );
}

export const REPORT_TABS = [
  { to: "/sales", label: "Sales" },
  { to: "/analytics", label: "Deep dive" },
];

/** Phone (canvas v2-om-nav-overview): Today is Overview plus the two Reports tabs; desktop keeps Reports' own pair. */
export function ReportTabs({ withOverview = false }: { withOverview?: boolean }) {
  const isDesktop = useIsDesktop();
  const { modules } = useInstalledModules();
  if (isDesktop) return withOverview ? null : <RouteTabs options={REPORT_TABS} />;
  if (!modules.has("reports")) return null;
  return <RouteTabs options={[{ to: "/", label: "Overview" }, ...REPORT_TABS]} />;
}
